import React, { useEffect, useState } from 'react';
import {
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  FormControl,
  FormLabel,
  Avatar,
  Input,
  Text,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import InputController from 'components/Form/InputController';
import { BANNER_POSITION, ModalType } from 'constants/common';
import { toast } from 'components/Toast';
import { useUpdateCategoryMutation, useCreateCategoryMutation, useUpdateThumbnailMutation } from 'services/category';
import { BannerFormValidate } from 'utils/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import SelectController from 'components/Form/SelectController';
import { useCreateBannerMutation, useUpdateBannerMutation, useUploadBannerMutation } from 'services/banner';

const errorDefault = {
  banner: '',
  bannerMobile: '',
};

const stateDefault = {
  banner: null,
  bannerMobile: null,
};

const optionPosition = Object.keys(BANNER_POSITION).map(st => {
  return {
    value: st,
    label: st,
  };
});

const CreateCategoryModal = ({ isOpen, categorysDetail, onClose, refetch }) => {
  const params = useParams();
  const { id: categoryId } = params || {};
  const cancelRef = React.useRef();
  const createBannerMutation = useCreateBannerMutation();
  const updateBannerMutation = useUpdateBannerMutation();
  const updateThumnailMutation = useUploadBannerMutation();
  const [file, setFile] = useState();
  const { control, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(BannerFormValidate),
    defaultValues: {
      url: '',
    },
  });
  const [error, setError] = useState(errorDefault);
  const fileInput = React.useRef(null);
  const fileMobileInput = React.useRef(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [bannerAds, setBannerAds] = useState(stateDefault);
  const [bannerMobilePreview, setBannerMobilePreview] = useState('');

  useEffect(() => {
    if (categorysDetail) {
      const positionDetail = optionPosition?.find(ele => ele?.value === categorysDetail.position);
      reset({
        link: categorysDetail.link,
        name: categorysDetail.name,
        id: categorysDetail.id,
        position: positionDetail ?? null,
      });
      setBannerMobilePreview(categorysDetail?.bannerUrl_mobile);
      setBannerPreview(categorysDetail?.bannerUrl);
      setBannerAds({
        banner: categorysDetail.bannerUrl,
        bannerMobile: categorysDetail.bannerUrl_mobile,
      });
    }
  }, [categorysDetail]);

  const handleSuccess = () => {
    toast.showMessageSuccess(`${categorysDetail ? 'Cập nhật' : 'Tạo'} Banner thành công`);
    refetch?.();
    onClose(ModalType.Add);
  };
  const handleError = error => {
    toast.showMessageError(
      error?.response?.data?.errors?.[0]?.msg || error?.response?.data?.msg || `${categorysDetail ? 'Cập nhật' : 'Tạo'} Banner thất bại`
    );
  };

  const clickEditButton = (bannerUpload, values) => {
    const adsData = {
      name: values.name,
      link: values.link,
      position: values.position?.value,
      ...bannerUpload,
    };

    try {
      if (!!categorysDetail) {
        updateBannerMutation.mutate(
          { ...adsData, id: categorysDetail._id },
          {
            onSuccess: res => {
              if (res?.code == 0) {
                toast.showMessageSuccess('Cập nhật banner thành công');
                refetch();
                onClose(ModalType.Add);
                setError(errorDefault);
              }
            },
            onError: error => {
              handleError(error);
            },
          }
        );
        return;
      }
      createBannerMutation.mutate(adsData, {
        onSuccess: res => {
          if (res?.code == 0) {
            toast.showMessageSuccess('Tạo banner thành công');
            refetch();
            onClose(ModalType.Add);
            setError(errorDefault);
          }
        },
        onError: error => {
          handleError(error);
        },
      });
    } catch (error) {
      toast.showMessageError('Tạo banner thất bại');
    }
  };

  const onSubmit = async values => {
    if (!categorysDetail) {
      if (!bannerAds?.banner) {
        setError({
          ...error,
          banner: 'Vui lòng tải ảnh',
        });
        return;
      }
      if (!bannerAds?.bannerMobile) {
        setError({
          ...error,
          bannerMobile: 'Vui lòng tải ảnh',
        });
        return;
      }
    }

    let dataUpload = {
      bannerUrl: '',
      bannerUrl_mobile: '',
    };
    if (typeof bannerAds.banner === 'object') {
      const adsData = new FormData();
      adsData.append('banner', bannerAds.banner);
      await updateThumnailMutation.mutateAsync(adsData, {
        onSuccess: res => {
          dataUpload = {
            ...dataUpload,
            bannerUrl: res?.data?.path,
          };
        },
        onError: error => {
          handleError(error);
        },
      });
    } else {
      dataUpload = {
        ...dataUpload,
        bannerUrl: bannerAds?.banner,
      };
    }
    if (typeof bannerAds.bannerMobile === 'object') {
      const adsDataMobile = new FormData();
      adsDataMobile.append('banner', bannerAds.bannerMobile);
      await updateThumnailMutation.mutateAsync(adsDataMobile, {
        onSuccess: res => {
          dataUpload = {
            ...dataUpload,
            bannerUrl_mobile: res?.data?.path,
          };
        },
        onError: error => handleError(error),
      });
    } else {
      dataUpload = {
        ...dataUpload,
        bannerUrl_mobile: bannerAds?.bannerMobile,
      };
    }
    // setLoading(true);
    clickEditButton(dataUpload, values);
  };

  const onChangeBanner = (isMobile = false) => {
    const btn = fileInput?.current;
    const btnMobile = fileMobileInput?.current;
    if (isMobile) {
      if (btnMobile !== null) {
        btnMobile.click();
      }
      return;
    }
    if (btn !== null) {
      btn.click();
    }
  };

  return (
    <>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onClose(ModalType.Add);
        }}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent maxW={'650px'}>
          <AlertDialogHeader textTransform="uppercase">{categorysDetail ? 'Cập nhật' : 'Tạo'} Banner</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <InputController control={control} name="name" label="Tên" />
            <InputController
              control={control}
              name="link"
              label="Đường dẫn"
              type="textarea"
              styleContainer={{
                marginTop: '1rem',
              }}
            />
            <SelectController
              label="Vị trí"
              name="position"
              placeholder="Chọn Vị trí"
              control={control}
              options={optionPosition}
              isRequired
              styleContainer={{ pt: '4' }}
            />
            <FormControl mt={3}>
              <FormLabel>
                Banner PC
                {/* <br />* Ratio Dimension For Popup Banner (w/h): 300/250 <br />*
                Ratio Dimension For Footer Banner (w/h): 320/50 */}
              </FormLabel>
              <Avatar
                src={bannerPreview}
                minW={bannerAds.position === BANNER_POSITION.POPUP ? 300 : 600}
                minH={bannerAds.position === BANNER_POSITION.POPUP ? 250 : 100}
                borderRadius="0"
                me="18px"
                mb={3}
                onClick={() => onChangeBanner()}
              />
              <Input
                hidden
                type="file"
                ref={fileInput}
                accept="image/*"
                onChange={event => {
                  setBannerAds({
                    ...bannerAds,
                    banner: event.target.files[0],
                  });
                  const image = window.URL.createObjectURL(event.target.files[0]);
                  setBannerPreview(image);
                  setError({
                    ...error,
                    banner: '',
                  });
                }}
              />
              {!!error.banner && <Text color={'red.300'}>{error.banner}</Text>}
            </FormControl>
            <FormControl mt={3}>
              <FormLabel>
                Banner Mobile
                {/* <br />* Ratio Dimension For Popup Banner (w/h): 300/250 <br />*
                Ratio Dimension For Footer Banner (w/h): 320/50 */}
              </FormLabel>
              <Avatar
                src={bannerMobilePreview}
                minW={bannerAds.position === BANNER_POSITION.POPUP ? 300 : 600}
                minH={bannerAds.position === BANNER_POSITION.POPUP ? 250 : 100}
                borderRadius="0"
                me="18px"
                mb={3}
                onClick={() => onChangeBanner(true)}
              />
              <Input
                hidden
                type="file"
                ref={fileMobileInput}
                accept="image/*"
                onChange={event => {
                  setBannerAds({
                    ...bannerAds,
                    bannerMobile: event.target.files[0],
                  });
                  const image = window.URL.createObjectURL(event.target.files[0]);
                  setBannerMobilePreview(image);
                  setError({
                    ...error,
                    bannerMobile: '',
                  });
                }}
              />
              {!!error.bannerMobile && <Text color={'red.300'}>{error.bannerMobile}</Text>}
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={() => {
                onClose(ModalType.Add);
              }}
            >
              Hủy
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              isLoading={createBannerMutation.isPending || updateBannerMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {categorysDetail ? 'Cập nhập' : 'Tạo'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CreateCategoryModal;
